import React from "react";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DownloadSimple } from "@phosphor-icons/react";
import { fetcher, fmtN, todayISO, downloadXlsx } from "./common";

function BilanTable({ rows, title, cols, testid }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
      <div className="px-4 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">{title}</div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            <th className="px-3 py-2 w-12">Réf</th><th className="px-3 py-2">Rubrique</th>
            {cols.map((c) => (<th key={c} className="px-3 py-2 text-right">{c}</th>))}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100" data-testid={testid}>
          {rows.map((r) => (
            <tr key={r.ref} className={r.net !== undefined && r.net === 0 && r.brut === 0 ? "text-zinc-500" : ""}>
              <td className="px-3 py-1.5 font-mono text-xs">{r.ref}</td>
              <td className="px-3 py-1.5">{r.label}</td>
              {r.net !== undefined ? (
                <>
                  <td className="px-3 py-1.5 text-right">{r.brut ? fmtN(r.brut) : ""}</td>
                  <td className="px-3 py-1.5 text-right">{r.amort ? fmtN(r.amort) : ""}</td>
                  <td className="px-3 py-1.5 text-right font-medium">{r.net ? fmtN(r.net) : ""}</td>
                </>
              ) : (
                <td className="px-3 py-1.5 text-right font-medium">{r.amount ? fmtN(r.amount) : ""}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** États de synthèse OHADA : bilan, compte de résultat, TAFIRE (rubriques SYSCOHADA ; mapping par classes pour COBAC). */
export default function EtatsOhada({ file }) {
  const year = new Date().getFullYear();
  const [from, setFrom] = React.useState(`${year}-01-01`);
  const [to, setTo] = React.useState(todayISO());
  const base = `/accounting/files/${file.id}/etats`;
  const { data: bilan, error: eBilan } = useSWR(`${base}/bilan?to_d=${to}`, fetcher);
  const { data: cr } = useSWR(`${base}/compte-resultat?from_d=${from}&to_d=${to}`, fetcher);
  const { data: tafire, error: eTafire } = useSWR(`${base}/tafire?from_d=${from}&to_d=${to}`, fetcher);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label>Du</Label><DatePicker value={from} onChange={setFrom} testid="etats-from" /></div>
        <div><Label>Au</Label><DatePicker value={to} onChange={setTo} testid="etats-to" /></div>
        {file.chart_type === "syscohada" && (
          <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/liasse?year=${to.slice(0, 4)}`, `liasse_dsf_${to.slice(0, 4)}_${file.name}.xlsx`)} data-testid="liasse-export">
            <DownloadSimple size={16} className="mr-1" /> Liasse DSF (classeur Excel)
          </Button>
        )}
      </div>

      <Tabs defaultValue="bilan">
        <TabsList>
          <TabsTrigger value="bilan" data-testid="etats-tab-bilan">Bilan</TabsTrigger>
          <TabsTrigger value="cr" data-testid="etats-tab-cr">Compte de résultat</TabsTrigger>
          <TabsTrigger value="tafire" data-testid="etats-tab-tafire">TAFIRE</TabsTrigger>
        </TabsList>

        <TabsContent value="bilan">
          {bilan && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className={`text-xs px-3 py-1.5 rounded-md ${bilan.equilibre ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid="bilan-equilibre">
                  {bilan.equilibre ? "Bilan équilibré ✓" : `Déséquilibre : ${fmtN(bilan.total_actif - bilan.total_passif)} ${file.currency}`}
                </div>
                <Button variant="outline" size="sm" onClick={() => downloadXlsx(`${base}/bilan?to_d=${to}&format=xlsx`, `bilan_${file.name}.xlsx`)} data-testid="bilan-export"><DownloadSimple size={14} className="mr-1" /> Excel</Button>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <BilanTable rows={bilan.actif} title={`Actif — Total ${fmtN(bilan.total_actif)} ${file.currency}`} cols={["Brut", "Amort./Dépr.", "Net"]} testid="bilan-actif" />
                <BilanTable rows={bilan.passif} title={`Passif — Total ${fmtN(bilan.total_passif)} ${file.currency}`} cols={["Montant"]} testid="bilan-passif" />
              </div>
            </div>
          )}
          {eBilan && <div className="text-sm text-red-700">Impossible de calculer le bilan.</div>}
        </TabsContent>

        <TabsContent value="cr">
          {cr && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => downloadXlsx(`${base}/compte-resultat?from_d=${from}&to_d=${to}&format=xlsx`, `compte_resultat_${file.name}.xlsx`)} data-testid="cr-export"><DownloadSimple size={14} className="mr-1" /> Excel</Button>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
                  <div className="px-4 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">Charges — {fmtN(cr.total_charges)} {file.currency}</div>
                  <table className="w-full text-sm"><tbody className="divide-y divide-zinc-100" data-testid="cr-charges">
                    {cr.charges.map((r) => (<tr key={r.ref}><td className="px-3 py-1.5 font-mono text-xs w-12">{r.ref}</td><td className="px-3 py-1.5">{r.label}</td><td className="px-3 py-1.5 text-right">{r.amount ? fmtN(r.amount) : ""}</td></tr>))}
                  </tbody></table>
                </div>
                <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
                  <div className="px-4 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">Produits — {fmtN(cr.total_produits)} {file.currency}</div>
                  <table className="w-full text-sm"><tbody className="divide-y divide-zinc-100" data-testid="cr-produits">
                    {cr.produits.map((r) => (<tr key={r.ref}><td className="px-3 py-1.5 font-mono text-xs w-12">{r.ref}</td><td className="px-3 py-1.5">{r.label}</td><td className="px-3 py-1.5 text-right">{r.amount ? fmtN(r.amount) : ""}</td></tr>))}
                  </tbody></table>
                </div>
              </div>
              <div className={`border rounded-md p-6 ${cr.resultat >= 0 ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="cr-resultat">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Résultat net de l'exercice</div>
                <div className="font-serif-display text-3xl mt-1">{fmtN(cr.resultat)} {file.currency} {cr.resultat >= 0 ? "(bénéfice)" : "(perte)"}</div>
              </div>
              <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
                <div className="px-4 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">Soldes intermédiaires de gestion</div>
                <table className="w-full text-sm"><tbody className="divide-y divide-zinc-100">
                  {cr.sig.map((s) => (<tr key={s.label}><td className="px-4 py-2">{s.label}</td><td className="px-4 py-2 text-right font-medium">{fmtN(s.amount)}</td></tr>))}
                </tbody></table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="tafire">
          {eTafire && (
            <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center text-sm text-zinc-500" data-testid="tafire-unavailable">
              {eTafire?.response?.data?.detail || "Le TAFIRE est disponible en plan SYSCOHADA uniquement."}
            </div>
          )}
          {!eTafire && tafire && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => downloadXlsx(`${base}/tafire?from_d=${from}&to_d=${to}&format=xlsx`, `tafire_${file.name}.xlsx`)} data-testid="tafire-export"><DownloadSimple size={14} className="mr-1" /> Excel</Button>
              </div>
              <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
                <table className="w-full text-sm"><tbody className="divide-y divide-zinc-100" data-testid="tafire-rows">
                  {tafire.rows.map((r, i) => (
                    <tr key={`${i}-${r.label}`} className={r.bold ? "bg-zinc-50 font-medium" : ""}>
                      <td className="px-4 py-2">{r.label}</td>
                      <td className="px-4 py-2 text-right">{fmtN(r.amount)}</td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
              <div className={`text-xs px-3 py-2 rounded-md inline-block ${Math.abs(tafire.controle.ecart) < 0.02 ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="tafire-controle">
                Contrôle : variation réelle de trésorerie {fmtN(tafire.controle.var_tresorerie_reelle)} — écart {fmtN(tafire.controle.ecart)} {file.currency}
              </div>
              <p className="text-xs text-zinc-500">{tafire.note}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
